import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addAsset, resetAddStatus } from './assetSlice';
import { fetchPortfolios, fetchAssetsByPortfolio } from '../portfolio/portfolioSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
} from '@mui/material';

const AddAsset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPortfolioId = searchParams.get('portfolioId');

  const [formData, setFormData] = useState({
    symbol: '',
    name: '',
    assetType: 'stock',
    portfolioId: preselectedPortfolioId || '',
    currency: 'USD',
  });

  const { portfolios, listPortfoliosStatus } = useSelector((state) => state.portfolio);
  const { addStatus, error } = useSelector((state) => state.asset);

  useEffect(() => {
    if (listPortfoliosStatus === 'idle') {
      dispatch(fetchPortfolios());
    }
  }, [dispatch, listPortfoliosStatus]);

  useEffect(() => {
    dispatch(resetAddStatus());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(addAsset(formData)).unwrap();
      
      // ✅ Immediately fetch updated assets for this portfolio
      dispatch(fetchAssetsByPortfolio({ portfolioId: formData.portfolioId }));
  
      navigate(`/portfolios/${formData.portfolioId}/assets`);
      dispatch(resetAddStatus());
    } catch (err) {
      console.error('Failed to add asset:', err);
    }
  };

  useEffect(() => {
    if (addStatus === 'succeeded') {
      navigate(`/portfolios/${formData.portfolioId}/assets`);
      dispatch(resetAddStatus());
    }
  }, [addStatus, navigate, dispatch, formData.portfolioId]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#E6F2F2"
    >
      <Paper
        elevation={3}
        sx={{ padding: 4, maxWidth: 400, width: '90%', borderRadius: 2 }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Add New Asset
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="Symbol"
            name="symbol"
            value={formData.symbol}
            onChange={handleInputChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Asset Type</InputLabel>
            <Select
              name="assetType"
              value={formData.assetType}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="stock">Stock</MenuItem>
              <MenuItem value="crypto">Crypto</MenuItem>
              <MenuItem value="currency">Currency</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Portfolio</InputLabel>
            <Select
              name="portfolioId"
              value={formData.portfolioId}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="" disabled>
                Select Portfolio
              </MenuItem>
              {portfolios.map((portfolio) => (
                <MenuItem key={portfolio.portfolioId} value={portfolio.portfolioId}>
                  {portfolio.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Currency</InputLabel>
            <Select
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="JPY">JPY</MenuItem>
              <MenuItem value="AUD">AUD</MenuItem>
            </Select>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={addStatus === 'loading'}
            sx={{ mt: 2 }}
          >
            {addStatus === 'loading' ? 'Adding...' : 'Add Asset'}
          </Button>
        </form>
        {error && <Typography color="error" align="center" mt={2}>{error}</Typography>}
      </Paper>
    </Box>
  );
};

export default AddAsset;